import { Hidden } from '@epilot/concorde-elements'
import { Grid, StackSpacing } from '@epilot/journey-elements'
import { clsx } from '@epilot/journey-logic-commons'
import type { Layout, LayoutProps, UISchemaElement } from '@jsonforms/core'
import { JsonFormsDispatch } from '@jsonforms/react'
import type { DetailedHTMLProps, HTMLAttributes } from 'react'
import { useMemo } from 'react'

import { useIsMobile } from '../../../utils/helper'
import { useStickyHeight } from '../../../utils/hooks/useStickyHeight'
import { useMainLinearLayoutStyles } from '../styles'
import {
  StickyZone,
  useWrapperStyles
} from '../TwoColumnsLayouts/MainContentCartLayout'
import { getStickyItems, stickySorter } from '../utils'

export type MainLinearLayoutOptions = Pick<
  SpacingProps,
  'alignItems' | 'scale' | 'children'
> & {
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
}

type SpacingProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  /**
   * Set the alignment of all the children as a group.
   */
  alignItems?: 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline'
  /**
   * Spacing scale to be applied.
   */
  scale: number
}

const MainLinearLayout = ({
  uischema,
  renderers,
  schema,
  path,
  enabled,
  visible
}: LayoutProps) => {
  const { wrapperStyles } = useWrapperStyles()

  const layout = uischema as Layout
  const {
    scale = 3,
    spacing = 4,
    alignItems = 'stretch',
    ...rest
  } = (uischema.options as MainLinearLayoutOptions) || {}

  const sticky: UISchemaElement[] = useMemo(() => {
    const items = getStickyItems(layout.elements)

    items.sort(stickySorter)

    return items
  }, [layout.elements])

  const mobile = useIsMobile()

  const { stickyRef, stickyHeight } = useStickyHeight(mobile)
  const classes = useMainLinearLayoutStyles({
    stickyHeight: sticky?.length ? stickyHeight : 0
  })

  return (
    <Hidden showChildren={visible}>
      <div className={clsx(classes.container, mobile && classes.withPadding)}>
        <div className={wrapperStyles}>
          <Grid container spacing={spacing} {...rest}>
            <Grid item xs={12}>
              <StackSpacing alignItems={alignItems} scale={scale} {...rest}>
                <>
                  {layout.elements.map((child, index) => {
                    if (child.options?.stickyOnMobile && mobile) {
                      return null
                    } else {
                      return (
                        <JsonFormsDispatch
                          enabled={enabled}
                          key={index}
                          path={path}
                          renderers={renderers}
                          schema={schema}
                          uischema={child}
                        />
                      )
                    }
                  })}
                </>
              </StackSpacing>
            </Grid>
          </Grid>
        </div>
        {mobile && sticky.length > 0 && (
          <StickyZone ref={stickyRef}>
            {sticky.map((child, index) => (
              <JsonFormsDispatch
                enabled={enabled}
                key={'footer' + index}
                path={path}
                renderers={renderers}
                schema={schema}
                uischema={child}
              />
            ))}
          </StickyZone>
        )}
      </div>
    </Hidden>
  )
}

export default MainLinearLayout
